import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "kiwami_admin_token";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = unknown / loading, false = not logged in, object = logged in
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Hydrate user on mount / token change.
  useEffect(() => {
    let cancelled = false;
    const fetchMe = async () => {
      if (!token) {
        setUser(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUser(data);
      } catch (err) {
        // Token rejected (expired / revoked / wrong secret) → log it once
        // for debug, then transparently log out.
        if (err?.response?.status && err.response.status !== 401) {
          console.warn("auth/me failed:", err?.response?.status, err?.message);
        }
        if (!cancelled) {
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch {
            /* ignore */
          }
          setToken(null);
          setUser(false);
        }
      }
    };
    fetchMe();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data?.requires_2fa) {
      // Step 1 OK — caller must complete step 2 with TOTP code.
      return { requires_2fa: true, challenge_token: data.challenge_token };
    }
    try {
      localStorage.setItem(STORAGE_KEY, data.access_token);
    } catch {
      /* ignore */
    }
    setToken(data.access_token);
    setUser(data.user);
    return { user: data.user };
  }, []);

  const login2FA = useCallback(async (challenge_token, code) => {
    const { data } = await axios.post(`${API}/auth/2fa/login`, {
      challenge_token,
      code,
    });
    try {
      localStorage.setItem(STORAGE_KEY, data.access_token);
    } catch {
      /* ignore */
    }
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(false);
  }, []);

  const authedAxios = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    if (token) {
      instance.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          logout();
        }
        return Promise.reject(err);
      },
    );
    return instance;
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ user, token, login, login2FA, logout, authedAxios }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
